// Journal.tsx — LES LOGS, sur leur propre écran.
//
// Jusqu'ici le seul journal visible était une tuile du tableau de bord (60 lignes, sans filtre ni
// recherche) et l'audit de l'IdP était enfoui dans un onglet de l'écran Utilisateurs — où la maquette
// l'avait placé à tort. Chercher « que s'est-il passé ? » n'est pas une tâche d'administration des
// comptes : ça mérite un écran, avec des filtres et une table qu'on trie.
//
// Deux journaux, deux natures, réunis ici parce qu'on les consulte pour la même raison :
//   · CONSOLE — ce que le plan de contrôle a FAIT (mises en service, blocages, corrections, flotte) ;
//   · IdP     — qui s'est CONNECTÉ, qui a échoué, quels droits ont bougé.
import { useEffect, useMemo, useState } from 'react';
import { api, type ActivityEvent, type IdpAuditEvent } from '../api';
import { DataTable, type DtRow } from '../data-table';
import { Chip } from '../ui';
import { AUDIT_FAMS, auditColor, auditFailed } from '../audit-format';
import { familyLabel } from '../event-families';
import { FamilyBadge } from '../family-badge';

/** Le fond d'une ligne d'échec — la teinte de la maquette (6 %), jamais le rouge plein. */
export const FAILED_ROW_TINT = 'color-mix(in srgb, var(--da-fg) 6%, transparent)';

/** Plafond demandé au serveur. Le TOTAL revient avec, pour que l'écran dise ce qu'il ne montre pas. */
const CONSOLE_LIMIT = 500;

// À la minute, année comprise (« 26/07/2026 18:41 ») : la seconde n'aide pas à relire un journal, elle l'alourdit.
const horodate = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

/** Familles d'événements Console — celles que le serveur écrit réellement dans `console_event`. */
const FAMILLES: ActivityEvent['family'][] = ['provisioning', 'flotte', 'blocages', 'données', 'clients', 'plateforme'];

/**
 * Les périodes du journal, et leur durée en heures — `null` : tout le journal.
 * ⚠️ FILTRÉES AU SERVEUR (`since`) : l'écran ne reçoit qu'une tête de journal, et seul le serveur atteint un événement
 *    plus ancien que ces lignes-là.
 */
export const JOURNAL_PERIODS: readonly { k: string; label: string; hours: number | null }[] = [
  { k: '24h', label: '24 h', hours: 24 },
  { k: '7j', label: '7 jours', hours: 168 },
  { k: '30j', label: '30 jours', hours: 720 },
  { k: 'tout', label: 'Tout', hours: null },
];

/** La borne basse d'une période, en ISO — `undefined` pour tout le journal. */
export function periodSince(hours: number | null, now: number): string | undefined {
  return hours === null ? undefined : new Date(now - hours * 3_600_000).toISOString();
}

// ── Journal de la CONSOLE ───────────────────────────────────────────────────────────────────────
function ConsoleJournal() {
  const [data, setData] = useState<{ events: ActivityEvent[]; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [fam, setFam] = useState<string>('tout');
  // Sept jours par défaut (maquette) : le journal du jour est souvent vide, l'intégralité noie ce qu'on cherche.
  const [period, setPeriod] = useState('7j');

  useEffect(() => {
    let alive = true;
    const hours = JOURNAL_PERIODS.find((p) => p.k === period)?.hours ?? null;
    // ⚠️ L'ancienne période s'efface pendant la lecture : ses lignes, laissées sous le nouveau sélecteur, se liraient
    //    comme le résultat de la nouvelle.
    setData(null);
    api.activity(CONSOLE_LIMIT, periodSince(hours, Date.now()))
      .then((r) => { if (alive) { setData({ events: r.events, total: r.total }); setErr(null); } })
      .catch((e) => { if (alive) { setData(null); setErr((e as Error).message); } });
    return () => { alive = false; };
  }, [period]);

  const rows = useMemo<DtRow[]>(() => {
    if (!data) return [];
    return data.events
      .filter((e) => fam === 'tout' || e.family === fam)
      .map((e, i) => ({
        id: `${e.at}-${i}`,
        cells: {
          at: { v: <span className="mono" style={{ fontSize: 'var(--t-body)', whiteSpace: 'nowrap' }}>{horodate(e.at)}</span>, sortValue: new Date(e.at).getTime(), text: horodate(e.at) },
          family: { v: <FamilyBadge family={e.family} />, text: familyLabel(e.family), sortValue: e.family },
          text: { v: <span style={{ fontSize: 'var(--t-body)' }}>{e.text}</span>, text: e.text },
          meta: { v: <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{e.meta || '—'}</span>, text: e.meta },
        },
      }));
  }, [data, fam]);

  // Le segmenté de période — la piste de la charte (`.seg-track`), posée dans la barre du tableau.
  const periodPicker = (
    <div className="seg-track" role="group" aria-label="Période">
      {JOURNAL_PERIODS.map((p) => (
        <button key={p.k} type="button" aria-pressed={period === p.k} className={period === p.k ? 'seg active' : 'seg'} onClick={() => setPeriod(p.k)}>{p.label}</button>
      ))}
    </div>
  );

  // ⚠️ Le sélecteur reste à l'écran pendant la lecture et après un échec : on doit voir QUELLE période se lit, et pouvoir
  //    en changer quand celle-ci échoue.
  if (err || !data) {
    return (
      <div className="dcard" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {err ? <div className="err">Journal indisponible : {err}</div> : <span className="muted">Lecture du journal…</span>}
        <div style={{ flex: 1 }} />
        {periodPicker}
      </div>
    );
  }

  // ⚠️ Le plafond se DIT. Sans cette ligne, un écran montrant 500 lignes sur 12 000 se lit comme
  // l'intégralité du journal — et on conclurait qu'un événement plus ancien n'a jamais eu lieu.
  const tronque = data.total > data.events.length;

  return (
    <>
      <DataTable
        cols={[
          { key: 'at', label: 'Date', sortable: true },
          { key: 'family', label: 'Famille', sortable: true },
          { key: 'text', label: 'Événement' },
          { key: 'meta', label: 'Acteur / détail' },
        ]}
        rows={rows}
        unit="ligne"
        searchPlaceholder="Événement, acteur, détail…"
        pageSize={25}
        emptyIcon="history"
        emptyTitle="Aucune ligne"
        emptyNote="Aucun événement ne correspond à ce filtre sur cette période."
        headRight={periodPicker}
        headExtra={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chip on={fam === 'tout'} onClick={() => setFam('tout')}>tout</Chip>
            {FAMILLES.map((f) => <Chip key={f} on={fam === f} onClick={() => setFam(f)}>{familyLabel(f)}</Chip>)}
          </div>
        }
      />
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 8 }}>
        {tronque
          ? <><b>{data.events.length}</b> lignes les plus récentes sur <b>{data.total}</b> dans cette période — les plus anciennes ne sont pas chargées.</>
          : period === 'tout'
            ? <>Journal complet : <b>{data.total}</b> ligne{data.total > 1 ? 's' : ''}.</>
            : <>Toute la période : <b>{data.total}</b> ligne{data.total > 1 ? 's' : ''}.</>}
      </div>
    </>
  );
}

// ── Journal d'AUDIT de l'IdP ────────────────────────────────────────────────────────────────────
function IdpJournal() {
  const [events, setEvents] = useState<IdpAuditEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [fam, setFam] = useState('tout');

  useEffect(() => {
    api.identityAudit()
      .then((r) => { setEvents(r.events); setErr(null); })
      .catch((e) => { setEvents(null); setErr((e as Error).message); });
  }, []);

  const rows = useMemo<DtRow[]>(() => {
    if (!events) return [];
    const matcher = AUDIT_FAMS.find((f) => f.k === fam) ?? AUDIT_FAMS[0]!;
    return events.filter((e) => matcher.match(e.action)).map((e) => ({
      id: String(e.id),
      // Un échec se voit à la LIGNE, pas seulement au libellé : une série se repère d'un coup d'œil.
      // ⚠️ Une TEINTE, pas la couleur pleine : le fond rouge plein rendait la ligne illisible.
      tint: auditFailed(e.action) ? FAILED_ROW_TINT : undefined,
      cells: {
        at: { v: <span className="mono" style={{ fontSize: 'var(--t-body)', whiteSpace: 'nowrap' }}>{horodate(e.at)}</span>, sortValue: new Date(e.at).getTime(), text: horodate(e.at) },
        action: { v: <span className="mono" style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: auditColor(e.action) }}>{e.action}</span>, text: e.action, sortValue: e.action },
        email: { v: <span style={{ fontSize: 'var(--t-body)' }}>{e.email ?? '—'}</span>, text: e.email ?? '' },
        ip: { v: <span className="mono" style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{e.ip ?? '—'}</span>, text: e.ip ?? '' },
      },
    }));
  }, [events, fam]);

  if (err) return <div className="dcard" style={{ padding: 18 }}><div className="err">Journal IdP indisponible : {err}</div></div>;
  if (!events) return <div className="dcard" style={{ padding: 18 }}><span className="muted">Chargement du journal…</span></div>;

  return (
    <>
      <DataTable
        cols={[
          { key: 'at', label: 'Date', sortable: true },
          { key: 'action', label: 'Action', sortable: true },
          { key: 'email', label: 'Compte' },
          { key: 'ip', label: 'IP' },
        ]}
        rows={rows}
        unit="ligne"
        searchPlaceholder="Action, compte, IP…"
        pageSize={25}
        emptyIcon="lock"
        emptyTitle="Aucune ligne d’audit"
        emptyNote="Aucun événement ne correspond à ce filtre."
        headExtra={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {AUDIT_FAMS.map((f) => <Chip key={f.k} on={fam === f.k} onClick={() => setFam(f.k)}>{f.label}</Chip>)}
          </div>
        }
      />
      {/* ⚠️ Ce que la Console reçoit de l'IdP est une TÊTE de journal, pas le journal : le dire évite
          de conclure qu'un événement absent de l'écran n'a jamais eu lieu. */}
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 8 }}>
        Derniers événements transmis par l’IdP, tous comptes confondus — le journal complet vit dans l’IdP.
      </div>
    </>
  );
}

export function Journal() {
  const [source, setSource] = useState<'console' | 'idp'>('console');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Journal</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
            Ce que le plan de contrôle a fait, et qui s’est connecté. Deux journaux distincts, jamais fusionnés.
          </div>
        </div>
      </div>

      {/* Deux SOURCES, pas deux vues d'une même donnée : les mélanger dans une seule table donnerait
          une chronologie fausse (l'IdP n'envoie qu'une tête de journal, la Console son historique). */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        {([['console', 'Activité de la Console'], ['idp', 'Audit des connexions (IdP)']] as const).map(([k, label]) => (
          <button aria-pressed={source === k}
            key={k}
            type="button"
            onClick={() => setSource(k)}
            className={source === k ? 'tab active' : 'tab'}
          >
            {label}
          </button>
        ))}
      </div>

      {source === 'console' ? <ConsoleJournal /> : <IdpJournal />}
    </>
  );
}
